/**
 * OutreachWorkspace — unified Outreach surface for Triveny.
 *
 * The three separate sidebar entries (Visitor ID, Lead Builder, Cold Outreach)
 * collapse into one — this wrapper tabs across them so the user navigates a
 * single coherent funnel (Inbound → Outbound → Sequences → Funnel) instead
 * of three disconnected pages.
 *
 * Implementation: mounts the existing pages as-is, no JSX changes inside
 * them — only the chrome is new. Sidebar routes "platform-outreach" here
 * (added in App.tsx). The previous sidebar entries are preserved as
 * deep-link routes so existing URLs / launcher actions keep working; they
 * just deep-link into the matching tab.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { Eye, Target, Send, BarChart3, ArrowRight, RefreshCw, Brain, Bell, Check } from 'lucide-react';
import { tvTokens } from '../../shared/components/triveny';
import { config as productConfig } from '@/src/config';
import { VisitorIdentificationPage } from './VisitorIdentificationPage';
import { LeadBuilderPage } from './LeadBuilderPage';
import { ColdOutreachPage } from './ColdOutreachPage';
import { IntentIntelligencePage } from './IntentIntelligencePage';
import {
  visitorIdentificationApi,
  leadBuilderApi,
  coldOutreachApi,
  salesApi,
  marketingApi,
} from '../../shared/services/platformApi';

export type OutreachTab = 'funnel' | 'activity' | 'visitors' | 'leadbuilder' | 'intelligence' | 'sequences';

interface OutreachWorkspaceProps {
  /** Open at a specific sub-surface — passed from sidebar / deep-links. */
  initialTab?: OutreachTab;
}

const TABS: { id: OutreachTab; label: string; sub: string; icon: typeof Eye }[] = [
  { id: 'funnel', label: 'Funnel', sub: 'Cross-channel roll-up', icon: BarChart3 },
  { id: 'activity', label: 'Activity', sub: 'Funnel notifications', icon: Bell },
  { id: 'visitors', label: 'Inbound', sub: 'Visitor ID', icon: Eye },
  { id: 'leadbuilder', label: 'Outbound', sub: 'Lead Builder · ICP', icon: Target },
  { id: 'intelligence', label: 'Intelligence', sub: 'Intent & signals', icon: Brain },
  { id: 'sequences', label: 'Sequences', sub: 'Cold Outreach', icon: Send },
];

export function OutreachWorkspace({ initialTab = 'funnel' }: OutreachWorkspaceProps = {}) {
  const { t } = useAppTranslation();
  const isTriveny = productConfig.isTriveny;
  const [active, setActive] = useState<OutreachTab>(initialTab);
  const [unread, setUnread] = useState(0);

  // Keep state in sync if the parent (App.tsx) re-mounts us with a different
  // initialTab — e.g. when the user clicks a deep link from another surface.
  useEffect(() => {
    setActive(initialTab);
  }, [initialTab]);

  // Poll unread funnel-activity count so the Activity tab can badge it.
  const loadUnread = useCallback(async () => {
    try {
      const res: any = await marketingApi.getNotifications();
      const data = res?.data ?? res ?? {};
      setUnread(data.unread || 0);
    } catch {
      /* silent */
    }
  }, []);
  useEffect(() => {
    void loadUnread();
    const id = window.setInterval(loadUnread, 60000);
    return () => window.clearInterval(id);
  }, [loadUnread]);

  // On WeaveIn we never render this wrapper — the legacy three-pages IA is
  // preserved. App.tsx only routes here when isTriveny.
  if (!isTriveny) {
    return null;
  }

  return (
    <div
      className="triveny-platform"
      style={{
        minHeight: '100vh',
        background: tvTokens.paper,
        fontFamily: tvTokens.fontSans,
        color: tvTokens.ink,
      }}
    >
      {/* No page banner — the TvPillarBar ("MARKET · Funnel") is the header (like the
          Design page). The sub-tab navigator below is this workspace's own chrome. */}

      {/* Sub-tab navigator — full-bleed under the pillar bar, mono labels.
          The outer row must NOT clip overflow (the bell dropdown is absolutely
          positioned and would be hidden by an overflow:auto ancestor), so only
          the inner tablist scrolls; the bell lives outside it. */}
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-end',
          padding: '12px 22px 0',
          borderBottom: `1px solid ${tvTokens.borderMuted}`,
          background: tvTokens.surface,
        }}
      >
        <div
          style={{ display: 'flex', gap: 4, overflowX: 'auto', flex: 1, minWidth: 0 }}
          role="tablist"
          aria-label="Outreach sub-surfaces"
        >
        {TABS.map((tab) => {
          const isActive = active === tab.id;
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              type="button"
              role="tab"
              className="tv-underline-tab"
              aria-selected={isActive}
              onClick={() => setActive(tab.id)}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
                padding: '10px 16px 12px',
                background: 'transparent',
                border: 0,
                borderBottom: `2px solid ${isActive ? tvTokens.accent : 'transparent'}`,
                cursor: 'pointer',
                fontFamily: tvTokens.fontDisplay,
                fontSize: 13,
                fontWeight: 600,
                letterSpacing: '-0.005em',
                color: isActive ? tvTokens.ink : tvTokens.ink500,
                whiteSpace: 'nowrap',
                transition: 'color .12s ease, border-color .12s ease',
              }}
            >
              <Icon size={14} />
              <span>{tab.label}</span>
              {tab.id === 'activity' && unread > 0 && (
                <span
                  style={{
                    minWidth: 16,
                    height: 16,
                    padding: '0 4px',
                    borderRadius: 8,
                    background: '#7468ae',
                    color: '#fff',
                    fontSize: 10,
                    fontWeight: 700,
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {unread > 9 ? '9+' : unread}
                </span>
              )}
              <span
                style={{
                  fontFamily: tvTokens.fontMono,
                  fontSize: 10,
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                  color: isActive ? tvTokens.accentInk : tvTokens.ink500,
                }}
              >
                · {tab.sub}
              </span>
            </button>
          );
        })}
        </div>
      </div>

      {/* Active sub-surface — existing pages, mounted as-is. Each page already
          ships its own .triveny-platform overlay; nesting is benign. The sub-tab
          navigator above is the section header, so embedded pages suppress their
          own page banner (see IntentIntelligencePage `embedded`). */}
      <div>
        {active === 'funnel' && <OutreachFunnel />}
        {active === 'activity' && <ActivityPanel onUnreadChange={setUnread} />}
        {active === 'visitors' && <VisitorIdentificationPage />}
        {active === 'leadbuilder' && <LeadBuilderPage />}
        {active === 'intelligence' && <IntentIntelligencePage embedded />}
        {active === 'sequences' && <ColdOutreachPage />}
      </div>
    </div>
  );
}

/**
 * ActivityPanel — the funnel activity feed as a full tab (was the bell dropdown).
 * Lists marketing-funnel notifications (surge alerts, hot signals, NBA drafts,
 * opportunities, campaign replies, daily digests) with mark-all-read. Polls every
 * 60s and reports unread up to the workspace so the tab badge stays in sync.
 */
interface ActivityNotification {
  id: string;
  type: string;
  title: string;
  body?: string;
  level?: string;
  is_read?: boolean;
  created_at?: string;
}

function ActivityPanel({ onUnreadChange }: { onUnreadChange?: (n: number) => void }) {
  const [items, setItems] = useState<ActivityNotification[]>([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const res: any = await marketingApi.getNotifications();
      const data = res?.data ?? res ?? {};
      const list: ActivityNotification[] = Array.isArray(data.notifications) ? data.notifications : [];
      setItems(list);
      setUnread(data.unread || 0);
      onUnreadChange?.(data.unread || 0);
    } catch {
      /* silent */
    } finally {
      setLoading(false);
    }
  }, [onUnreadChange]);

  useEffect(() => {
    void load();
    const id = window.setInterval(load, 60000);
    return () => window.clearInterval(id);
  }, [load]);

  const markAllRead = async () => {
    try {
      await marketingApi.markAllNotificationsRead();
      setItems((prev) => prev.map((n) => ({ ...n, is_read: true })));
      setUnread(0);
      onUnreadChange?.(0);
    } catch {
      /* silent */
    }
  };

  const rel = (iso?: string) => {
    if (!iso) return '';
    const mins = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.round(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    return `${Math.round(hrs / 24)}d ago`;
  };

  return (
    <div style={{ maxWidth: 720, margin: '0 auto', padding: '24px 22px 60px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <div>
          <div
            style={{
              fontFamily: tvTokens.fontMono,
              fontSize: 10,
              letterSpacing: '0.16em',
              textTransform: 'uppercase',
              color: tvTokens.ink500,
              marginBottom: 6,
            }}
          >
            {unread > 0 ? `${unread} unread` : 'All caught up'}
          </div>
          <div style={{ fontFamily: tvTokens.fontDisplay, fontSize: 22, fontWeight: 600, letterSpacing: '-0.02em', color: tvTokens.ink }}>
            Funnel activity
          </div>
        </div>
        {unread > 0 && (
          <button
            onClick={markAllRead}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              padding: '7px 12px',
              background: 'transparent',
              border: `1px solid ${tvTokens.border}`,
              borderRadius: tvTokens.r,
              color: tvTokens.ink700,
              fontFamily: tvTokens.fontDisplay,
              fontSize: 12,
              fontWeight: 500,
              cursor: 'pointer',
            }}
          >
            <Check size={13} /> Mark all read
          </button>
        )}
      </div>

      {loading ? (
        <div style={{ padding: 32, color: tvTokens.ink400, display: 'flex', alignItems: 'center', gap: 8 }}>
          <RefreshCw size={15} style={{ animation: 'spin 1s linear infinite' }} /> Loading…
        </div>
      ) : items.length === 0 ? (
        <div
          style={{
            textAlign: 'center',
            padding: '48px 20px',
            border: `1px dashed ${tvTokens.border}`,
            borderRadius: tvTokens.rLg,
            background: tvTokens.surface,
          }}
        >
          <Bell size={26} color={tvTokens.ink400} />
          <p style={{ marginTop: 10, fontSize: 14, fontWeight: 500, color: tvTokens.ink700 }}>No activity yet</p>
          <p style={{ marginTop: 4, fontSize: 13, color: tvTokens.ink500 }}>
            Surge alerts, drafted actions, new opportunities, and replies show up here as the funnel runs.
          </p>
        </div>
      ) : (
        <div style={{ border: `1px solid ${tvTokens.border}`, borderRadius: tvTokens.rLg, overflow: 'hidden', background: tvTokens.surface }}>
          {items.map((n, i) => (
            <div
              key={n.id}
              style={{
                padding: '12px 16px',
                borderTop: i === 0 ? 'none' : `1px solid ${tvTokens.borderMuted}`,
                background: n.is_read ? 'transparent' : tvTokens.accentSoft,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                {n.level === 'high' && <span style={{ width: 7, height: 7, borderRadius: 4, background: '#f43f5e', flexShrink: 0 }} />}
                <span style={{ fontSize: 13.5, fontWeight: 600, color: tvTokens.ink }}>{n.title}</span>
                <span style={{ marginLeft: 'auto', fontSize: 11, color: tvTokens.ink400 }}>{rel(n.created_at)}</span>
              </div>
              {n.body && <p style={{ fontSize: 12.5, color: tvTokens.ink700, margin: '4px 0 0' }}>{n.body}</p>}
            </div>
          ))}
        </div>
      )}
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

/**
 * Cross-channel funnel — real roll-up from Visitor ID, Lead Builder,
 * Cold Outreach, and Sales Pipeline APIs. Each stage shows a count + the
 * conversion to the next stage; the side bar lists the source-by-source
 * breakdown so a user can see which channel is feeding each stage.
 */

interface FunnelStage {
  label: string;
  sublabel: string;
  count: number;
  source: string;
}

function OutreachFunnel() {
  const [loading, setLoading] = useState(true);
  const [reloadKey, setReloadKey] = useState(0);
  const [stages, setStages] = useState<FunnelStage[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const [visitorsRes, matchesRes, campaignsRes, opportunitiesRes] = await Promise.all([
          visitorIdentificationApi
            .getCompanies({ page: 1, page_size: 500 })
            .catch(() => ({ success: false, data: null }) as const),
          leadBuilderApi
            .getCompanyMatches({ limit: 500 })
            .catch(() => ({ success: false, data: null }) as const),
          coldOutreachApi
            .getCampaigns({ page: 1, page_size: 200 })
            .catch(() => ({ success: false, data: null }) as const),
          salesApi
            .getOpportunities({ limit: 500 } as any)
            .catch(() => ({ success: false, data: null }) as const),
        ]);

        if (cancelled) return;

        // ---- Stage 1: anonymous visits (Visitor ID totals across companies)
        const visitorData =
          (visitorsRes as any)?.success && (visitorsRes as any).data
            ? ((visitorsRes as any).data as {
                companies?: any[];
                total?: number;
              })
            : null;
        const visitorCompanies: any[] = visitorData?.companies ?? [];
        const totalVisits = visitorCompanies.reduce(
          (sum, c) => sum + (c.total_visits || 0),
          0,
        );
        const identifiedCompanies = visitorCompanies.length;
        const highIntentVisitors = visitorCompanies.filter(
          (c) => (c.intent_score || 0) >= 70,
        ).length;

        // ---- Stage 2: ICP-qualified accounts (Lead Builder matches)
        const matchData = (matchesRes as any)?.success && (matchesRes as any).data
          ? (() => {
              const d = (matchesRes as any).data;
              if (Array.isArray(d)) return d;
              if (Array.isArray(d?.matches)) return d.matches;
              if (Array.isArray(d?.company_matches)) return d.company_matches;
              return [] as any[];
            })()
          : [];
        const icpMatches = matchData.length;
        const icpQualified = matchData.filter((m: any) => (m.score || 0) >= 70).length;

        // ---- Stage 3: contacts engaged via Cold Outreach sequences
        const campaignData =
          (campaignsRes as any)?.success && (campaignsRes as any).data
            ? ((campaignsRes as any).data as { campaigns?: any[] })
            : null;
        const campaigns = campaignData?.campaigns ?? [];
        const totalProspects = campaigns.reduce(
          (sum: number, c: any) => sum + (c.total_prospects || 0),
          0,
        );
        const totalReplies = campaigns.reduce(
          (sum: number, c: any) => sum + (c.prospects_replied || 0),
          0,
        );
        const totalMeetings = campaigns.reduce(
          (sum: number, c: any) => sum + (c.meetings_booked || 0),
          0,
        );

        // ---- Stage 4: open opportunities on the sales pipeline
        const oppsData = (opportunitiesRes as any)?.success && (opportunitiesRes as any).data
          ? (() => {
              const d = (opportunitiesRes as any).data;
              if (Array.isArray(d)) return d;
              if (Array.isArray(d?.opportunities)) return d.opportunities;
              return [] as any[];
            })()
          : [];
        const openOpps = oppsData.filter(
          (o: any) => o.stage !== 'closed_won' && o.stage !== 'closed_lost',
        ).length;
        const wonOpps = oppsData.filter((o: any) => o.stage === 'closed_won').length;

        setStages([
          {
            label: 'Identified',
            sublabel: `${highIntentVisitors} hot · ${totalVisits.toLocaleString()} visits`,
            count: identifiedCompanies,
            source: 'Visitor ID',
          },
          {
            label: 'ICP qualified',
            sublabel: `${icpQualified} score ≥ 70`,
            count: icpMatches,
            source: 'Lead Builder',
          },
          {
            label: 'Engaged',
            sublabel: `${totalReplies} replies · ${totalMeetings} meetings booked`,
            count: totalProspects,
            source: 'Cold Outreach',
          },
          {
            label: 'In pipeline',
            sublabel: `${wonOpps} closed-won`,
            count: openOpps,
            source: 'Sales Pipeline',
          },
        ]);
      } catch (err: any) {
        if (!cancelled) {
          setError(err?.message || 'Could not load funnel');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [reloadKey]);

  if (loading) {
    return (
      <div style={{ padding: '32px 22px', color: tvTokens.ink500, fontFamily: tvTokens.fontSans }}>
        Loading funnel…
      </div>
    );
  }

  // Conversion rate between consecutive stages, with safe divide-by-zero.
  const conv = (a: number, b: number): string =>
    b <= 0 || a <= 0 ? '—' : `${Math.round((a / b) * 100)}%`;

  const maxCount = Math.max(1, ...stages.map((s) => s.count));

  return (
    <div style={{ padding: '24px 22px 40px' }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 18,
        }}
      >
        <div>
          <div
            style={{
              fontFamily: tvTokens.fontMono,
              fontSize: 10,
              letterSpacing: '0.16em',
              textTransform: 'uppercase',
              color: tvTokens.ink500,
              marginBottom: 6,
            }}
          >
            Live
          </div>
          <div
            style={{
              fontFamily: tvTokens.fontDisplay,
              fontSize: 22,
              fontWeight: 600,
              letterSpacing: '-0.02em',
              color: tvTokens.ink,
            }}
          >
            Cross-channel funnel
          </div>
        </div>
        <button
          onClick={() => setReloadKey((k) => k + 1)}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            padding: '7px 12px',
            background: 'transparent',
            border: `1px solid ${tvTokens.border}`,
            borderRadius: tvTokens.r,
            color: tvTokens.ink700,
            fontFamily: tvTokens.fontDisplay,
            fontSize: 12,
            fontWeight: 500,
            cursor: 'pointer',
          }}
          aria-label="Refresh funnel data"
        >
          <RefreshCw size={12} /> Refresh
        </button>
      </div>

      {error && (
        <div
          style={{
            background: tvTokens.attentionSoft,
            color: tvTokens.attentionInk,
            border: `1px solid ${tvTokens.attention}`,
            borderRadius: tvTokens.r,
            padding: '10px 14px',
            marginBottom: 18,
            fontSize: 13,
          }}
        >
          {error} — showing zeros where data was unavailable.
        </div>
      )}

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 1fr)',
          gap: 14,
          maxWidth: 880,
        }}
      >
        {stages.map((s, i) => {
          const nextCount = stages[i + 1]?.count ?? 0;
          const ratio = Math.max(0.18, s.count / maxCount);
          return (
            <React.Fragment key={s.label}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  background: tvTokens.surface,
                  border: `1px solid ${tvTokens.border}`,
                  borderRadius: tvTokens.rLg,
                  padding: '16px 20px',
                  boxShadow: tvTokens.shadow1,
                }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      fontFamily: tvTokens.fontMono,
                      fontSize: 10,
                      letterSpacing: '0.12em',
                      textTransform: 'uppercase',
                      color: tvTokens.ink500,
                      marginBottom: 4,
                    }}
                  >
                    {s.source}
                  </div>
                  <div
                    style={{
                      fontFamily: tvTokens.fontDisplay,
                      fontSize: 16,
                      fontWeight: 600,
                      color: tvTokens.ink,
                      marginBottom: 4,
                    }}
                  >
                    {s.label}
                  </div>
                  <div style={{ color: tvTokens.ink500, fontSize: 12 }}>{s.sublabel}</div>
                </div>
                <div
                  style={{
                    fontFamily: tvTokens.fontDisplay,
                    fontSize: 28,
                    fontWeight: 600,
                    letterSpacing: '-0.025em',
                    color: tvTokens.ink,
                    minWidth: 80,
                    textAlign: 'right',
                  }}
                >
                  {s.count.toLocaleString()}
                </div>
                <div
                  aria-hidden="true"
                  style={{
                    width: 80,
                    height: 8,
                    background: tvTokens.paper2,
                    borderRadius: 999,
                    overflow: 'hidden',
                  }}
                >
                  <div
                    style={{
                      width: `${ratio * 100}%`,
                      height: '100%',
                      background: tvTokens.accent,
                    }}
                  />
                </div>
              </div>
              {i < stages.length - 1 && (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    padding: '0 24px',
                    color: tvTokens.ink500,
                    fontFamily: tvTokens.fontMono,
                    fontSize: 11,
                    letterSpacing: '0.06em',
                  }}
                >
                  <ArrowRight size={12} />
                  <span>{conv(nextCount, s.count)} conversion</span>
                </div>
              )}
            </React.Fragment>
          );
        })}
      </div>

      <div
        style={{
          marginTop: 24,
          fontSize: 12,
          color: tvTokens.ink500,
          maxWidth: 720,
          lineHeight: 1.5,
        }}
      >
        Live data from Visitor ID, Lead Builder ICP matches, Cold Outreach
        campaigns, and the Sales Pipeline. Each stage shows the count fed by
        its source; conversion ratios use the next stage's count relative
        to this one. Re-run after new signals land to see the funnel shift.
      </div>
    </div>
  );
}

export default OutreachWorkspace;
